import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Shield, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { adminLogin, clearError } from '../../redux/auth/authSlice';
import { RootState } from '../../redux/store';
import Image from '../../components/common/Image';
import {
  TaskyAlert,
  TaskyAuthLayout,
  TaskyButton,
  TaskyFormGroup,
  TaskyInput,
} from '../../components/tasky';

const AdminLoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState({ email: false, password: false });

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, error, user } = useSelector((s: RootState) => s.auth);

  useEffect(() => { dispatch(clearError()); }, [dispatch]);
  useEffect(() => {
    if (isAuthenticated && user?.role === 'admin') navigate('/admin');
  }, [isAuthenticated, user, navigate]);

  const validate = () => {
    const errs: { email?: string; password?: string } = {};
    if (!email) errs.email = 'メールアドレスは必須です';
    else if (!/\S+@\S+\.\S+/.test(email)) errs.email = '有効なメールアドレスを入力してください';
    if (!password) errs.password = 'パスワードは必須です';
    return errs;
  };
  const errs = validate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ email: true, password: true });
    if (Object.keys(errs).length > 0) return;
    try {
      await dispatch(adminLogin({ email, password })).unwrap();
    } catch (err) {
      console.error('Admin login failed:', err);
    }
  };

  return (
    <TaskyAuthLayout
      logo={
        <div className="text-center">
          <Image category="common" fileName="logo.png" alt="ロゴ" className="h-10 w-auto mx-auto" />
          <p className="text-sm text-gray-500 mt-2 mb-3">広告クリエイティブAIアシスタント</p>
          <div className="inline-flex items-center gap-1.5 bg-warm-surface border border-warm-border rounded-full px-3 py-1">
            <Shield className="w-3.5 h-3.5 text-dark" />
            <span className="text-xs font-medium text-dark tracking-wide">管理者ログイン</span>
          </div>
        </div>
      }
      footer={
        <Link
          to="/login"
          className="inline-flex items-center gap-1 text-gray-500 hover:text-dark transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          通常のログインへ戻る
        </Link>
      }
    >
      {error && (
        <TaskyAlert tone="error" className="mb-4">
          {error}
        </TaskyAlert>
      )}

      <form onSubmit={handleSubmit} noValidate className="space-y-5">
        <TaskyFormGroup
          label="メールアドレス"
          required
          htmlFor="email"
          error={touched.email ? errs.email : undefined}
        >
          <TaskyInput
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, email: true }))}
            placeholder="admin@example.com"
            autoComplete="email"
            required
            hasError={touched.email && Boolean(errs.email)}
          />
        </TaskyFormGroup>

        <TaskyFormGroup
          label="パスワード"
          required
          htmlFor="password"
          error={touched.password ? errs.password : undefined}
        >
          <div className="relative">
            <TaskyInput
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, password: true }))}
              placeholder="パスワードを入力"
              autoComplete="current-password"
              required
              className="pr-10"
              hasError={touched.password && Boolean(errs.password)}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'パスワードを非表示' : 'パスワードを表示'}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </TaskyFormGroup>

        <TaskyButton type="submit" isLoading={isLoading} className="w-full">
          {isLoading ? 'ログイン中...' : 'ログイン'}
        </TaskyButton>
      </form>
    </TaskyAuthLayout>
  );
};

export default AdminLoginPage;
